import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { MdSearch } from 'react-icons/md';
import { FaUsers } from 'react-icons/fa';

import { IPaginate } from 'shared/interfaces';
import { IUser } from 'pages/User/interfaces';

import {
  InputSearch,
  ButtonLink,
  HeaderBackground,
  HeaderPage,
  HeaderInfo,
  Table,
  Pagination,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Position,
  Button,
  Loader,
  TableResponsive,
  PopUpMenu,
  Can,
} from '../../../components';

import { ModalHandles } from '../../../components/Modal';

import { useToast } from '../../../contexts';
import { getUsers, deleteUser } from '../../../services/api/user';

import * as S from './styles';

const UserList: React.FC = () => {
  const [users, setUsers] = useState<IPaginate<IUser>>();
  const [selectedUser, setSelectedUser] = useState<IUser>();
  const modalRemoverRef = useRef<ModalHandles>(null);

  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const { addToast } = useToast();
  const history = useHistory();

  const handleSearch = useCallback(
    async ({ page, per_page }) => {
      try {
        setLoading(true);

        const params = {
          page,
          per_page,
          term,
        };

        const response = await getUsers(params);

        setUsers(response);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Ops...',
          description: 'Ops, ocorreu um erro no processo de pesquisa',
        });
      } finally {
        setLoading(false);
      }
    },
    [term, addToast],
  );

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 20,
    };

    handleSearch(params);
  }, [handleSearch]);

  const handleRemove = async (): Promise<void> => {
    try {
      deleteUser(selectedUser?.id || '');

      if (!users) return;
      const newList = users.data.filter((item) => item.id !== selectedUser?.id);

      setUsers({ ...users, data: newList });

      modalRemoverRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Usuario removido com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao inserir um novo registro',
      });
    }
  };

  const handleOpenModalRemove = useCallback((userSelected: IUser): void => {
    setSelectedUser(userSelected);
    modalRemoverRef.current?.openModal();
  }, []);

  return (
    <S.Container>
      <HeaderBackground>
        <HeaderPage>
          <HeaderInfo title="Usuários" icon={FaUsers} />

          <S.ContainerSearch>
            <InputSearch
              title="Digite aqui o que deseja pesquisar"
              icon={MdSearch}
              placeholder="Pesquisar"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter'
                  ? handleSearch({ page: 1, per_page: users?.per_page })
                  : {}
              }
            />
          </S.ContainerSearch>

          <div>
            <Can checkPermission="users_create">
              <ButtonLink title="Adicionar usuário" to="/users/add">
                Adicionar
              </ButtonLink>
            </Can>
          </div>
        </HeaderPage>
      </HeaderBackground>

      <S.Panel>
        {loading ? (
          <Loader loading={loading} align="center" message="Carregando..." />
        ) : (
          <>
            <TableResponsive>
              <Table>
                <thead>
                  <tr>
                    <th className="text-left">Usuário</th>
                    <th className="text-left">Email</th>
                    <th className="text-left">Permissão</th>
                    <th className="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {users &&
                    users.data.map((item: IUser) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.roles?.name}</td>
                        <td className="text-center">
                          <PopUpMenu>
                            <Can checkPermission="users_update">
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(`/users/edit/${item.id}`)
                                }
                              >
                                <S.IconEdit
                                  title="Editar"
                                  size={24}
                                  color="#707070"
                                />
                                Editar
                              </button>
                            </Can>

                            <Can checkPermission="users_delete">
                              <button
                                type="button"
                                onClick={() => handleOpenModalRemove(item)}
                              >
                                <S.IconDelete
                                  title="Remover"
                                  size={24}
                                  color="#707070"
                                />
                                Remover
                              </button>
                            </Can>
                          </PopUpMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </TableResponsive>
            <S.FooterContainer>
              {users && (
                <Pagination
                  page={users?.page}
                  total={users?.total}
                  per_page={users?.per_page}
                  last_page={users?.last_page}
                  onSearch={handleSearch}
                />
              )}
            </S.FooterContainer>
          </>
        )}
      </S.Panel>

      <Modal ref={modalRemoverRef} options={{ width: '400px' }}>
        <ModalHeader>
          <ModalTitle>Você Confirma a EXCLUSÃO?</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p>O registro será removido completamente!</p>
        </ModalBody>
        <ModalFooter>
          <Position align="right">
            <Button
              variant="outline-secondary"
              onClick={() => modalRemoverRef.current?.closeModal()}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => handleRemove()}
              style={{ marginLeft: '16px' }}
            >
              SIM, confirmo a exclusão
            </Button>
          </Position>
        </ModalFooter>
      </Modal>
    </S.Container>
  );
};

export default UserList;
